import { MantineCheckbox } from '../mantine-controls/MantineCheckbox';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import React from 'react';
import { isBooleanControl, RankedTester, rankWith, isDescriptionHidden } from '@jsonforms/core';
import { Box, Text, Tooltip } from '@mantine/core';
import { ControlProps } from '../../types/controlProps';
import { withJsonFormsControlProps } from '../utils/withJsonFormsPropsMappers';
import { useVisibilityLevelContext } from '../../contexts/VisibilityLevelContext';
import { getDefaultValue, useSelectedObject } from '../../hooks/useSelectedObject';
import { useIsParentSelected } from '../../hooks/useIsParentSelected';

export const MantineBooleanControl = ({
  data,
  visible,
  label,
  id,
  enabled,
  uischema,
  schema,
  rootSchema,
  handleChange,
  errors,
  path,
  config,
  description,
}: ControlProps) => {
  const { isLevelEnabled } = useVisibilityLevelContext();
  const { isParentSelected } = useIsParentSelected(path);
  const disabled = !isParentSelected;
  const fallbackValue = disabled ? false : getDefaultValue(path);

  const propertyLevel = schema.level;
  const isValid = errors.length === 0;
  const appliedUiSchemaOptions = merge({}, config, uischema.options);

  const showDescription = !isDescriptionHidden(
    visible,
    description,
    // Checkboxes do not receive focus until they are used, so
    // we cannot rely on focus as criteria for showing descriptions.
    // So we pass "false" to treat it as unfocused.
    false,
    appliedUiSchemaOptions.showUnfocusedDescription
  );

  const firstFormHelperText = showDescription ? description : !isValid ? errors : null;
  const secondFormHelperText = showDescription && !isValid ? errors : null;

  const tooltipId = `${id}-tip`;
  const helpId1 = `${id}-help1`;
  const helpId2 = `${id}-help2`;

  if (!visible || !isLevelEnabled(propertyLevel)) {
    return null;
  }

  return (
    <Box>
      <Tooltip id={tooltipId} label={description ?? ''} position='top-start' offset={15}>
        <Box display={'flex'}>
          <fieldset disabled={disabled}>
            <MantineCheckbox
              id={`${id}-input`}
              isValid={isEmpty(errors)}
              data={data ?? fallbackValue}
              enabled={enabled}
              visible={visible}
              path={path}
              uischema={uischema}
              schema={schema}
              rootSchema={rootSchema}
              handleChange={handleChange}
              errors={errors}
              config={config}
              label={label}
            />
          </fieldset>
        </Box>
      </Tooltip>
      <Text id={helpId1} c={!isValid && !showDescription ? 'red.6' : 'gray.6'}>
        {firstFormHelperText}
      </Text>
      <Text id={helpId2} c={!isValid ? 'red.6' : 'gray.6'}>
        {secondFormHelperText}
      </Text>
    </Box>
  );
};

export const mantineBooleanControlTester: RankedTester = rankWith(2, isBooleanControl);
export default withJsonFormsControlProps(MantineBooleanControl);
